// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { HttpService } from './http.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { API_KEY } from '@app/core/environment.configuration';

import {
  Role,
  RoleDetail,
  RoleGroupSelections,
  RoleObject,
  RoleUserSelections,
  ScopeCategory,
  ScopeConstraint
} from '@app/core/models';
import { getAdminScope, isScopeAdmin } from '../utilities/scopes.helpers';

@Injectable()
export class RolesService {
  constructor(private httpClient: HttpClient, private httpService: HttpService, @Inject(API_KEY) private api: string) {}

  public get(roleId: string): Observable<RoleDetail> {
    const route = `${this.api}/roles/${roleId}`;
    return this.httpClient.get<RoleDetail>(route);
  }

  public getList(): Observable<Role[]> {
    const route = `${this.api}/roles`;
    return this.httpClient.get<RolesListResponse>(route).pipe(map((response: RolesListResponse) => response.roles));
  }

  public getListByOrganization(orgId: string): Observable<Role[]> {
    const route = `${this.api}/accounts/${orgId}/roles`;
    return this.httpClient.get<RolesListResponse>(route).pipe(map((response: RolesListResponse) => response.roles));
  }

  public getListPagination(offset: number = 0, limit: number = 10): Observable<Role[]> {
    const route = `${this.api}/roles?offset=${offset}&limit=${limit}`;
    return this.httpClient.get<RolesListResponse>(route).pipe(map((response: RolesListResponse) => response.roles));
  }

  public addUsers(roleId: string, userSelections: RoleUserSelections) {
    const route = `${this.api}/roleuserscope`;
    const payload = this.getRoleUserScopeRequest(roleId, userSelections);
    return this.httpClient.post(route, payload);
  }

  public updateUserScope(roleId: string, userSelections: RoleUserSelections) {
    const route = `${this.api}/roleuserscope`;
    const payload = this.getRoleUserScopeRequest(roleId, userSelections);
    return this.httpClient.post(route, payload);
  }

  public addGroups(roleId: string, sections: RoleGroupSelections) {
    const route = `${this.api}/rolegroup`;
    const payload = this.getRoleUserScopeRequest(roleId, sections);
    return this.httpClient.post(route, payload);
  }

  public createRole(createRoleRequest: CreateRoleRequest) {
    const route = `${this.api}/roles`;
    return this.httpClient.post(route, createRoleRequest);
  }

  public updateRole(roleId: string, updateRoleRequest: UpdateRoleRequest) {
    const route = `${this.api}/roles/` + roleId;
    return this.httpClient.put(route, updateRoleRequest);
  }

  removeByUserName(roleId: string, roleUpdateRequest: UpdateRoleDetailRequest) {
    // if there is username, this means we delete user grant (remove user role)
    const route = `${this.api}/roleuserscope/user`;
    const delRoleUserRequest: DeleteUserRoleScopeByUsernameRequest = {
      role_id: roleId,
      username: roleUpdateRequest.username
    };
    return this.httpClient.request('DELETE', route, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: delRoleUserRequest
    });
  }

  removeByGrantId(roleId: string, roleUpdateRequest: UpdateRoleDetailRequest) {
    // if there is grantId, this means we delete user grant (remove user role)
    const route = `${this.api}/roleuserscope`;
    const delRoleUserRequest: DeleteUserRoleRequest = { role_id: roleId, grant_id: roleUpdateRequest.grantId };
    return this.httpClient.request('DELETE', route, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: delRoleUserRequest
    });
  }

  public updateRoleByPolicyIds(roleId: string, roleUpdateRequest: UpdateRoleDetailRequest) {
    const route = `${this.api}/roles/${roleId}/policies`;
    return this.httpClient.post(route, roleUpdateRequest);
  }

  public updateRoleDetail(roleId: string, roleUpdateRequest: UpdateRoleDetailRequest) {
    if (roleUpdateRequest.username) {
      return this.removeByUserName(roleId, roleUpdateRequest);
    }
    if (roleUpdateRequest.grantId) {
      return this.removeByGrantId(roleId, roleUpdateRequest);
    }

    if (roleUpdateRequest.policyIds) {
      this.updateRoleByPolicyIds(roleId, roleUpdateRequest);
    }

    const route = `${this.api}/roles/${roleId}`;
    return this.httpClient.put(route, roleUpdateRequest);
  }

  public deleteRole(roleId: string) {
    const route = `${this.api}/roles/${roleId}`;
    return this.httpClient.delete(route);
  }

  public removeRolePolicy(roleId: string, policyId: string) {
    const route = `${this.api}/roles/${roleId}/policies/${policyId}`;
    return this.httpClient.delete(route);
  }

  public getScopeCategories(): Observable<ScopeCategory[]> {
    const route = `${this.api}/roles/scopes/types`;
    return this.httpClient.get<ScopeCategory[]>(route);
  }

  public getScopeConstraints(scopeCategoryId: string, limit = 100, search = ''): Observable<ScopeConstraint[]> {
    const route = `${this.api}/roles/scopes/${scopeCategoryId}?limit=${limit}&search=${search}`;
    return this.httpClient.get<ScopeConstraint[]>(route);
  }

  public getProjects({ limit = 100, search = '' }): Observable<ScopeConstraint[]> {
    const route = `${this.api}/roles/scopes/1?limit=${limit}&search=${search}`;
    return this.httpClient.get<ScopeConstraint[]>(route);
  }

  public getProjectScopes({
    projectId = '',
    limit = 1000
    // search = ''
  }): Observable<ScopeConstraint[]> {
    const route = `${this.api}/roles/scopes/16?projectid=${projectId}&limit=${limit}`;
    return this.httpClient.get<ScopeConstraint[]>(route);
  }

  public createRoleMeetingCommittees({
    usernames = [],
    roleId = '',
    projectId = '',
    committeeIds = [],
    scopeId = 16
  }: {
    usernames: string[];
    roleId: string;
    projectId: string;
    committeeIds: string[];
    scopeId?: number;
  }) {
    const route = `${this.api}/roleuserscope`;
    const objIdRow = (innerProjectId: string, committeeId: string) => `${innerProjectId}|${committeeId}`;
    const payload = {
      roleId: roleId,
      object_type_id: scopeId,
      usernames: usernames,
      object_ids: [...committeeIds.map(committeeId => objIdRow(projectId, committeeId))]
    };
    return this.httpClient.post(route, payload);
  }

  public getRoleObjects(roleId: string): Observable<RoleObject[]> {
    const route = `${this.api}/roles/${roleId}/objects`;
    return this.httpClient.get<RoleObject[]>(route);
  }

  private getRoleUserScopeRequest(
    roleId: string,
    userSelections: RoleUserSelections
  ): RoleUserScopeRequest | undefined {
    if (!userSelections) {
      return undefined;
    }

    const { users, category } = userSelections;
    if (!users || !category) {
      return undefined;
    }

    const constraints = userSelections.constraints || [];
    const adminScope = getAdminScope();
    const isAdminCategory = isScopeAdmin(category.name);

    const usernames = users.map(user => user.username);
    const object_type_id = Number(category.id);
    const object_ids = isAdminCategory ? adminScope : constraints.map(item => item.id);
    return {
      roleId,
      usernames,
      object_ids,
      object_type_id
    };
  }

  public addGroupsToRole(roleId: string, data: RoleGroupSelections, scope: any) {
    const request: AddGroupsToRoleRequest = {
      role_id: roleId,
      group_ids: (data && data.groups && data.groups.map(group => group.group_id)) || [],
      object_ids: (data && data.constraints && data.constraints.map(row => row.id)) || [],
      object_type_id: +scope.id
    };

    const route = `${this.api}/rolegroup`;
    return this.httpClient.post(route, request);
  }

  public removeRoleGrantId(roleId: string, grantID: string) {
    const route = `${this.api}/rolegroup`;
    const request: DeleteRoleGroupRequest = {
      role_id: roleId,
      grant_id: grantID
    };

    return this.httpClient.request('DELETE', route, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: request
    });
  }

  public getRoleGroups(roleId: string) {
    return this.httpService.get(this.httpService.urls.roles.roleGroups, { urlParam: roleId });
  }

  public removeRoleGroup(roleId: string, groupId: string) {
    return this.httpService.delete(this.httpService.urls.roles.roleGroupsByID, { urlMultiParams: [roleId, groupId] });
  }

  public getRoleGroupScopes(roleId: string, groupId: string): Observable<RoleGroupScope[]> {
    return this.httpService.get(this.httpService.urls.roles.roleGroupScopes, { urlMultiParams: [roleId, groupId] });
  }

  public getRoleUsers(roleId: string): Observable<RoleUser[]> {
    return this.httpService.get(this.httpService.urls.roles.roleUsers, { urlParam: roleId });
  }

  public getRoleUserScopes(roleId: string, username: string): Observable<RoleUserScopeReponse[]> {
    return this.httpService.get(this.httpService.urls.roles.roleUserScopes, { urlMultiParams: [roleId, username] });
  }

  public getRolePolicies(roleId: string): Observable<RolePolicy[]> {
    return this.httpService.get(this.httpService.urls.roles.rolePolicies, { urlParam: roleId });
  }
}

// Response from /roles/-/policies
export interface RolePolicy {
  id: string;
  policy_id: string;
  policy_name: string;
  created_at: string;
  created_by: string;
}

// Response from /roles/-/users/-/scopes
export interface RoleUserScopeReponse {
  email: string;
  firstname: string;
  grant_id: string;
  lastname: string;
  object_id: string;
  object_name: string;
  object_type_id: string;
  object_type_name: string;
  scope_id: string;
  user_id: string;
  username: string;
}

// Response from /roles/-/users
export interface RoleUser {
  account_name: string;
  email: string;
  firstname: string;
  lastname: string;
  user_id: string;
  username: string;
}

interface DeleteRoleGroupRequest {
  role_id: string;
  grant_id: string;
}

interface AddGroupsToRoleRequest {
  role_id: string;
  group_ids: string[];
  object_ids?: string[] | any;
  object_type_id: any;
}

interface RolesListResponse {
  roles: Role[];
}

export interface RoleUserScopeRequest {
  roleId: string;
  usernames: string[];
  object_ids: string[];
  object_type_id: number;
}

interface CreateRoleRequest {
  roleName: string;
  roleTitle: string;
  roleDescription: string;
  policyIds?: string[];
  role_objects?: RoleObject[];
}

interface UpdateRoleRequest {
  roleName: string;
  roleTitle: string;
  roleDescription: string;
  role_objects?: RoleObject[];
}

interface UpdateRoleDetailRequest {
  roleName?: string;
  roleDescription?: string;
  policyIds?: string[];
  userIds?: string[];
  username?: string;
  grantId?: string;
}

interface DeleteUserRoleRequest {
  role_id?: string;
  grant_id?: string;
}

interface DeleteUserRoleScopeByUsernameRequest {
  role_id?: string;
  username?: string;
}

// Response from /role/-/groups
export interface RoleGroup {
  group_id: string;
  group_name: string;
  account_id: string;
  account_name: string;
  description: string;
  created_at: number;
  updated_at: number;
  created_by: string;
  updated_by: string;
}

// Response from /role/-/groups/-/scopes
export interface RoleGroupScope {
  grant_id: string;
  role_id: string;
  group_id: string;
  scope_id: string;
  group_name: string;
  object_id: string;
  object_name: string;
  object_type_id: string;
  object_type_name: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}



// WEBPACK FOOTER //
// ./src/app/core/services/roles.service.ts